import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormGroup,
  Grid,
} from '@forgeglobal/design-framework';
import { Typography } from '@mui/material';
import { useStyles } from './style';
import {
  ReportPreferences as ApiReportPreferences,
  ReportCadence,
  CADENCE_OPTIONS,
} from '@tcs/client-portal-api/types/ReportPreferences';

export type ReportPreferences = ApiReportPreferences;

interface ReportPreferencesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (preferences: ReportPreferences) => void;
  initialPreferences?: ReportPreferences;
}

const REPORT_TYPES = [
  {
    key: 'accounts',
    label: 'Accounts Reports',
    description:
      "Choose how often you'd like to receive account reports, which include balances, fee status, RMD details, and address indicators for each investor.",
  },
  {
    key: 'positions',
    label: 'Positions Reports',
    description:
      "Choose how often you'd like to receive cash reports, which show detailed investment holdings per account, including units, valuations, maturity information, and distress indicators.",
  },
  {
    key: 'transactions',
    label: 'Transaction Reports',
    description:
      "Choose how often you'd like to receive transaction reports, which show all activity across accounts — including asset IDs and recurring distribution requests.",
  },
  {
    key: 'beneficiary',
    label: 'Beneficiary Reports',
    description:
      "Choose how often you'd like to receive beneficiary reports, which outline account beneficiary details — including names, types, levels, and share percentages.",
  },
] as const;

const ReportPreferencesModal = ({
  open,
  onClose,
  onSave,
  initialPreferences = {
    representativeCode: '',
    accounts: [],
    positions: [],
    transactions: [],
    beneficiary: [],
  },
}: ReportPreferencesModalProps) => {
  const classes = useStyles();
  const [preferences, setPreferences] =
    useState<ReportPreferences>(initialPreferences);

  useEffect(() => {
    if (open) {
      setPreferences(initialPreferences);
    }
  }, [open, initialPreferences]);

  const handleCadenceChange = (
    reportType: keyof ReportPreferences,
    cadence: ReportCadence,
    checked: boolean
  ) => {
    setPreferences((prev) => {
      const currentCadences = prev[reportType] as ReportCadence[];
      const newCadences = checked
        ? [...currentCadences, cadence]
        : currentCadences.filter((c) => c !== cadence);

      return {
        ...prev,
        [reportType]: newCadences,
      };
    });
  };

  const handleClose = () => {
    setPreferences(initialPreferences);
    onClose();
  };
  const handleSave = () => {
    onSave(preferences);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
      className={classes.dialog}>
      <DialogTitle onClose={handleClose}>Report Preferences</DialogTitle>

      <DialogContent className={classes.wrapper}>
        <Box className={classes.content}>
          <Grid container>
            {REPORT_TYPES.map((reportType) => (
              <Grid
                item
                md={6}
                key={reportType.key}
                pl={4}
                pb={4}
                pr={4}
                pt={0}>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color="text.primary"
                  gutterBottom>
                  {reportType.label}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-test-id={`${reportType.key}-description`}
                  gutterBottom>
                  {reportType.description}
                </Typography>
                <FormGroup className={classes.formGroup}>
                  {CADENCE_OPTIONS.filter(
                    (cadence) => cadence !== 'Unknown'
                  ).map((cadence) => (
                    <FormControlLabel
                      key={cadence}
                      className={classes.formLabel}
                      control={
                        <Checkbox
                          checked={preferences[reportType.key].includes(
                            cadence
                          )}
                          onChange={(e) =>
                            handleCadenceChange(
                              reportType.key,
                              cadence,
                              e.target.checked
                            )
                          }
                          data-test-id={`${
                            reportType.key
                          }-${cadence.toLowerCase()}-checkbox`}
                        />
                      }
                      label={<Typography variant="body2">{cadence}</Typography>}
                    />
                  ))}
                </FormGroup>
              </Grid>
            ))}
          </Grid>
        </Box>
        <DialogActions className={classes.action}>
          <Button
            variant="outlined"
            onClick={handleClose}
            data-test-id="report-preferences-cancel-button">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            data-test-id="report-preferences-save-button">
            Save Changes
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default ReportPreferencesModal;
